/**
 * Portfolio Transactions Page (Web)
 * Displays transaction history from all broker accounts
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this component only displays pre-calculated data.
 * All portfolio calculations are performed by backend Portfolio Service.
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Select,
  TableSortLabel,
  Menu,
  Card,
  Collapse,
  Divider,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { usePortfolioTransactions } from '@shared/features/portfolio';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { useAuth } from '@shared/features/auth';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { formatCurrency, formatNumber, formatDateTime } from '@shared/utils/formatters';
import { exportToExcel, exportToPDF } from '@shared/utils/export';

export function PortfolioTransactionsPage() {
  const { accounts } = useBrokerAccounts();
  const { firebase_uid } = useAuth();
  const [selectedBrokerAccount, setSelectedBrokerAccount] = useState<string>('');
  const [transactionType, setTransactionType] = useState<'buy' | 'sell' | ''>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());

  const {
    transactions,
    total,
    loading,
    error,
    refresh,
  } = usePortfolioTransactions({
    brokerAccountId: selectedBrokerAccount || undefined,
    transactionType: transactionType || undefined,
    limit,
    offset: (page - 1) * limit,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    autoRefresh: false,
  });

  useEffect(() => {
    refresh();
  }, [selectedBrokerAccount, transactionType, page, fromDate, toDate]);

  const handleRefresh = async () => {
    await refresh();
  };

  const handleExportExcel = async () => {
    try {
      const columns = [
        { key: 'date', label: 'Date' },
        { key: 'type', label: 'Type' },
        { key: 'symbol', label: 'Symbol' },
        { key: 'brokerAccount', label: 'Broker Account' },
        { key: 'broker', label: 'Broker' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'price', label: 'Price' },
        { key: 'value', label: 'Value' },
        { key: 'charges', label: 'Charges' },
        { key: 'netAmount', label: 'Net Amount' },
      ];

      const exportData = transactions.map((transaction) => ({
        date: formatDateTime(transaction.transaction_date, { includeTime: false }),
        type: transaction.transaction_type.toUpperCase(),
        symbol: transaction.symbol || 'N/A',
        brokerAccount: (() => {
          const firebaseUid = transaction.firebase_uid?.trim();
          let brokerAccountId = transaction.broker_account_id?.trim();
          
          // If broker_account_id is a UUID, look up the actual broker_account_id
          if (brokerAccountId && uuidToBrokerAccountId.has(brokerAccountId)) {
            brokerAccountId = uuidToBrokerAccountId.get(brokerAccountId)!;
          }
          
          // Use composite key: firebase_uid:broker_account_id
          if (firebaseUid && brokerAccountId) {
            const compositeKey = `${firebaseUid}:${brokerAccountId}`;
            return accountNameMap.get(compositeKey) || 'N/A';
          }
          
          return 'N/A';
        })(),
        broker: transaction.broker_type || 'N/A',
        quantity: transaction.quantity || 0,
        price: transaction.price || 0,
        value: transaction.transaction_value !== undefined
          ? transaction.transaction_value
          : transaction.quantity * transaction.price,
        charges: transaction.total_charges !== undefined ? transaction.total_charges : '—',
        netAmount: transaction.net_amount !== undefined ? transaction.net_amount : '—',
      }));

      await exportToExcel(exportData, columns, `portfolio-transactions-${new Date().toISOString().split('T')[0]}`);
      setDownloadMenuAnchor(null);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const columns = [
        { key: 'date', label: 'Date' },
        { key: 'type', label: 'Type' },
        { key: 'symbol', label: 'Symbol' },
        { key: 'brokerAccount', label: 'Broker Account' },
        { key: 'broker', label: 'Broker' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'price', label: 'Price' },
        { key: 'value', label: 'Value' },
        { key: 'charges', label: 'Charges' },
        { key: 'netAmount', label: 'Net Amount' },
      ];

      const exportData = transactions.map((transaction) => ({
        date: formatDateTime(transaction.transaction_date, { includeTime: false }),
        type: transaction.transaction_type.toUpperCase(),
        symbol: transaction.symbol || 'N/A',
        brokerAccount: (() => {
          const firebaseUid = transaction.firebase_uid?.trim();
          let brokerAccountId = transaction.broker_account_id?.trim();
          
          // If broker_account_id is a UUID, look up the actual broker_account_id
          if (brokerAccountId && uuidToBrokerAccountId.has(brokerAccountId)) {
            brokerAccountId = uuidToBrokerAccountId.get(brokerAccountId)!;
          }
          
          // Use composite key: firebase_uid:broker_account_id
          if (firebaseUid && brokerAccountId) {
            const compositeKey = `${firebaseUid}:${brokerAccountId}`;
            return accountNameMap.get(compositeKey) || 'N/A';
          }
          
          return 'N/A';
        })(),
        broker: transaction.broker_type || 'N/A',
        quantity: transaction.quantity || 0,
        price: formatCurrency(transaction.price),
        value: transaction.transaction_value !== undefined
          ? formatCurrency(transaction.transaction_value)
          : formatCurrency(transaction.quantity * transaction.price),
        charges: transaction.total_charges !== undefined ? formatCurrency(transaction.total_charges) : '—',
        netAmount: transaction.net_amount !== undefined ? formatCurrency(transaction.net_amount) : '—',
      }));

      await exportToPDF(exportData, columns, `portfolio-transactions-${new Date().toISOString().split('T')[0]}`, 'Portfolio Transactions');
      setDownloadMenuAnchor(null);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    }
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const toggleAccountExpansion = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  const totalPages = Math.ceil(total / limit);

  // Create a map of firebase_uid:broker_account_id to account_name
  // Also create a UUID to broker_account_id mapping for transactions that have UUIDs
  const { accountNameMap, uuidToBrokerAccountId } = useMemo(() => {
    const map = new Map<string, string>();
    const uuidMap = new Map<string, string>(); // Map UUID (account.id) to broker_account_id
    
    accounts.forEach((account) => {
      const nameValue = account.account_name || account.broker_name || 'Unknown Account';
      
      // Map UUID to broker_account_id for lookup
      uuidMap.set(account.id, account.broker_account_id);
      
      // Create composite key: firebase_uid:broker_account_id
      if (firebase_uid) {
        const compositeKey = `${firebase_uid}:${account.broker_account_id}`;
        map.set(compositeKey, nameValue);
      }
    });
    
    // Also handle transactions that might have different firebase_uid
    if (transactions.length > 0) {
      const uniqueFirebaseUids = [...new Set(transactions.map(t => t.firebase_uid).filter(Boolean))];
      uniqueFirebaseUids.forEach(uid => {
        accounts.forEach((account) => {
          const nameValue = account.account_name || account.broker_name || 'Unknown Account';
          const compositeKey = `${uid}:${account.broker_account_id}`;
          map.set(compositeKey, nameValue);
        });
      });
    }
    
    
    return { accountNameMap: map, uuidToBrokerAccountId: uuidMap };
  }, [accounts, transactions, firebase_uid]);

  // Group transactions by broker_account_id
  const groupedTransactions = transactions.reduce((acc, transaction) => {
    const accountId = transaction.broker_account_id;
    if (!acc[accountId]) {
      acc[accountId] = [];
    }
    acc[accountId].push(transaction);
    return acc;
  }, {} as Record<string, typeof transactions>);

  // Calculate totals for each broker account
  const accountTotals = Object.entries(groupedTransactions).reduce((acc, [accountId, accountTransactions]) => {
    const buyTransactions = accountTransactions.filter(t => t.transaction_type.toLowerCase() === 'buy');
    const sellTransactions = accountTransactions.filter(t => t.transaction_type.toLowerCase() === 'sell');
    
    acc[accountId] = {
      totalTransactions: accountTransactions.length,
      buyCount: buyTransactions.length,
      sellCount: sellTransactions.length,
      totalValue: accountTransactions.reduce((sum, t) => {
        const value = t.transaction_value !== undefined ? t.transaction_value : (t.quantity * t.price);
        return sum + value;
      }, 0),
      totalCharges: accountTransactions.reduce((sum, t) => sum + (t.total_charges || 0), 0),
      totalNetAmount: accountTransactions.reduce((sum, t) => sum + (t.net_amount || 0), 0),
    };
    return acc;
  }, {} as Record<string, { 
    totalTransactions: number; 
    buyCount: number; 
    sellCount: number; 
    totalValue: number; 
    totalCharges: number; 
    totalNetAmount: number;
  }>);

  // Sorting function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <AuthenticatedLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Filters */}
        {accounts.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Broker Account</InputLabel>
                  <Select
                    value={selectedBrokerAccount}
              label="Broker Account"
                    onChange={(e: SelectChangeEvent<string>) => {
                setSelectedBrokerAccount(e.target.value);
                setPage(1);
              }}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: '4px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Accounts</MenuItem>
              {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id} sx={{ fontSize: '0.75rem' }}>
                  {account.account_name || account.broker_name}
                </MenuItem>
              ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Transaction Type</InputLabel>
                  <Select
                    value={transactionType}
              label="Transaction Type"
                    onChange={(e: SelectChangeEvent<string>) => {
                setTransactionType(e.target.value as 'buy' | 'sell' | '');
                setPage(1);
              }}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: '4px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Types</MenuItem>
                    <MenuItem value="buy" sx={{ fontSize: '0.75rem' }}>Buy</MenuItem>
                    <MenuItem value="sell" sx={{ fontSize: '0.75rem' }}>Sell</MenuItem>
                  </Select>
                </FormControl>

            <TextField
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
                  sx={{
                    height: '32px',
                    fontSize: '0.75rem',
                    '& .MuiInputBase-root': {
                      height: '32px',
                      fontSize: '0.75rem',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      py: '4px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem',
                    },
                  }}
            />

            <TextField
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
                  sx={{
                    height: '32px',
                    fontSize: '0.75rem',
                    '& .MuiInputBase-root': {
                      height: '32px',
                      fontSize: '0.75rem',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      py: '4px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem',
                    },
                  }}
            />
            </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {transactions.length > 0 && (
                  <>
                    <Tooltip title="Download">
                      <IconButton
                        onClick={handleDownloadMenuOpen}
                        disabled={loading}
                        color="primary"
                        size="small"
                        sx={{ 
                          border: '1px solid', 
                          borderColor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                        }}
                      >
                        <FileDownloadIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={downloadMenuAnchor}
                      open={Boolean(downloadMenuAnchor)}
                      onClose={handleDownloadMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem 
                        onClick={handleExportExcel}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        EXCEL
                      </MenuItem>
                      <MenuItem 
                        onClick={handleExportPDF}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        PDF
                      </MenuItem>
                    </Menu>
                  </>
                )}
                <Tooltip title="Refresh">
                  <IconButton
              onClick={handleRefresh}
              disabled={loading}
                    color="primary"
                    size="small"
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'primary.main',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>

                {(selectedBrokerAccount || transactionType || fromDate || toDate) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedBrokerAccount('');
                      setTransactionType('');
                      setFromDate('');
                      setToDate('');
                      setPage(1);
                    }}
                    startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      height: '32px',
                      px: 1.5,
                    }}
                  >
                    Clear
            </Button>
                )}
          </Box>
            </Stack>
        </Paper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && transactions.length > 0 && (
          <>
            <Stack spacing={2}>
              {Object.entries(groupedTransactions).map(([accountId, accountTransactions]) => {
                const firstTransaction = accountTransactions[0];
                const firebaseUid = firstTransaction?.firebase_uid?.trim();
                let brokerAccountId = accountId;
                
                // If accountId is a UUID, look up the actual broker_account_id
                if (accountId && uuidToBrokerAccountId.has(accountId)) {
                  brokerAccountId = uuidToBrokerAccountId.get(accountId)!;
                }
                
                // Use composite key: firebase_uid:broker_account_id
                const compositeKey = firebaseUid && brokerAccountId ? `${firebaseUid}:${brokerAccountId}` : null;
                const accountName = compositeKey ? accountNameMap.get(compositeKey) || 'N/A' : 'N/A';
                // brokerAccountId is the actual broker account identifier (not UUID)
                const isExpanded = expandedAccounts.has(accountId);
                const totals = accountTotals[accountId];
                const brokerType = accountTransactions[0]?.broker_type || 'N/A';

                // Sort transactions within this account
                const sortedAccountTransactions = [...accountTransactions].sort((a, b) => {
                  if (!orderBy) return 0;
                  
                  let aValue: any;
                  let bValue: any;

                  switch (orderBy) {
                    case 'date':
                      aValue = new Date(a.transaction_date).getTime();
                      bValue = new Date(b.transaction_date).getTime();
                      break;
                    case 'type':
                      aValue = (a.transaction_type || '').toLowerCase();
                      bValue = (b.transaction_type || '').toLowerCase();
                      break;
                    case 'symbol':
                      aValue = (a.symbol || '').toLowerCase();
                      bValue = (b.symbol || '').toLowerCase();
                      break;
                    case 'quantity':
                      aValue = a.quantity || 0;
                      bValue = b.quantity || 0;
                      break;
                    case 'price':
                      aValue = a.price || 0;
                      bValue = b.price || 0;
                      break;
                    case 'value':
                      aValue = a.transaction_value !== undefined ? a.transaction_value : (a.quantity * a.price);
                      bValue = b.transaction_value !== undefined ? b.transaction_value : (b.quantity * b.price);
                      break;
                    case 'charges':
                      aValue = a.total_charges || 0;
                      bValue = b.total_charges || 0;
                      break;
                    case 'netAmount':
                      aValue = a.net_amount || 0;
                      bValue = b.net_amount || 0;
                      break;
                    default:
                      return 0;
                  }

                  if (aValue < bValue) {
                    return order === 'asc' ? -1 : 1;
                  }
                  if (aValue > bValue) {
                    return order === 'asc' ? 1 : -1;
                  }
                  return 0;
                });

                return (
                  <Card key={accountId} elevation={2} sx={{ overflow: 'visible' }}>
                    {/* Broker Account Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(4, 55, 123, 0.05)',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(4, 55, 123, 0.08)',
                        },
                      }}
                      onClick={() => toggleAccountExpansion(accountId)}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                          <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              toggleAccountExpansion(accountId);
                            }}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              width: 32,
                              height: 32,
                            }}
                          >
                            {isExpanded ? (
                              <RemoveIcon sx={{ fontSize: '1.25rem' }} />
                            ) : (
                              <AddIcon sx={{ fontSize: '1.25rem' }} />
                            )}
                          </IconButton>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#04377b' }}>
                                {accountName}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                ({brokerAccountId})
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={brokerType}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6875rem',
                                  textTransform: 'capitalize',
                                  bgcolor: 'rgba(4, 55, 123, 0.1)',
                                  color: '#04377b',
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {totals.totalTransactions} {totals.totalTransactions === 1 ? 'transaction' : 'transactions'}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                                {totals.buyCount} Buy
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'error.main' }}>
                                {totals.sellCount} Sell
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={4} alignItems="center">
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                              Total Value
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#04377b' }}>
                              {formatCurrency(totals.totalValue)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                              Total Charges
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#04377b' }}>
                              {formatCurrency(totals.totalCharges)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                              Net Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#04377b' }}>
                              {formatCurrency(totals.totalNetAmount)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Box>

                    {/* Transactions Table (Collapsible) */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider />
                      <TableContainer>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' } }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.03)' }}>
                              <TableCell sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5, px: 3 }}>
                                <TableSortLabel
                                  active={orderBy === 'date'}
                                  direction={orderBy === 'date' ? order : 'asc'}
                                  onClick={() => handleRequestSort('date')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Date
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'type'}
                                  direction={orderBy === 'type' ? order : 'asc'}
                                  onClick={() => handleRequestSort('type')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Type
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'symbol'}
                                  direction={orderBy === 'symbol' ? order : 'asc'}
                                  onClick={() => handleRequestSort('symbol')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Symbol
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'quantity'}
                                  direction={orderBy === 'quantity' ? order : 'asc'}
                                  onClick={() => handleRequestSort('quantity')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Quantity
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'price'}
                                  direction={orderBy === 'price' ? order : 'asc'}
                                  onClick={() => handleRequestSort('price')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Price
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'value'}
                                  direction={orderBy === 'value' ? order : 'asc'}
                                  onClick={() => handleRequestSort('value')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Value
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charges'}
                                  direction={orderBy === 'charges' ? order : 'asc'}
                                  onClick={() => handleRequestSort('charges')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Charges
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#04377b', fontSize: '0.8125rem', py: 1.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'netAmount'}
                                  direction={orderBy === 'netAmount' ? order : 'asc'}
                                  onClick={() => handleRequestSort('netAmount')}
                                  sx={{ 
                                    '& .MuiTableSortLabel-icon': { 
                                      fontSize: '0.875rem',
                                      color: '#04377b !important',
                                    },
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    color: '#04377b',
                                  }}
                                >
                                  Net Amount
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedAccountTransactions.map((transaction) => {
                              const isBuy = transaction.transaction_type.toLowerCase() === 'buy';
                              return (
                                <TableRow
                                  key={transaction.id}
                                  sx={{
                                    '&:nth-of-type(even)': { bgcolor: 'rgba(4, 55, 123, 0.02)' },
                                    '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.05)' },
                                  }}
                                >
                                  <TableCell sx={{ py: 1.5, px: 3 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                      {formatDateTime(transaction.transaction_date, { includeTime: false })}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 1.5 }}>
                                    <Chip
                                      label={transaction.transaction_type.toUpperCase()}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.6875rem',
                                        fontWeight: 600,
                                        bgcolor: isBuy ? 'success.main' : 'error.main',
                                        color: 'white',
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                      {transaction.symbol || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                      {formatNumber(transaction.quantity, 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                      {formatCurrency(transaction.price)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                      {transaction.transaction_value !== undefined
                                        ? formatCurrency(transaction.transaction_value)
                                        : formatCurrency(transaction.quantity * transaction.price)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                      {transaction.total_charges !== undefined
                                        ? formatCurrency(transaction.total_charges)
                                        : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                      {transaction.net_amount !== undefined
                                        ? formatCurrency(transaction.net_amount)
                                        : '—'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Card>
                );
              })}
            </Stack>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_event: React.ChangeEvent<unknown>, value: number) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {transactions.length} of {total} transactions
              </Typography>
            </Box>
          </>
        )}

        {!loading && transactions.length === 0 && !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Transactions Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No transactions found for the selected filters.
            </Typography>
          </Paper>
        )}
      </Container>
    </AuthenticatedLayout>
  );
}
